import { inject, injectable } from "inversify";
import { StorageKey, IStorage, StorageTypes, StorageValue, StorageCollection, StorageCollectionValue } from "./storage.constants";
import { BgCommands } from "./bgCommands";
import { FrontLogger } from "./frontLogger/frontLogger";
import { backgroundClient } from "./background/client";

@injectable()
export class BackgroundStorage implements IStorage {
  constructor(@inject(FrontLogger) private readonly logger: FrontLogger) {}
  async get<K extends StorageKey | StorageKey[]>(key: K) {
    if (!key) {
      const err = new Error("missing key");
      this.logger.error({ err }, "missing key");
      throw err;
    }
    return await backgroundClient.send(BgCommands.GET_FROM_STORAGE, { key });
  }
  async set<K extends StorageKey | Partial<StorageTypes>>(key: K, data?: K extends StorageKey ? StorageValue<K> : never) {
    if (!key) {
      const err = new Error("missing key");
      this.logger.error({ err }, "missing key");
      throw err;
    }
    await backgroundClient.send(BgCommands.SAVE_TO_STORAGE, { key, data });
  }
  async remove<K extends StorageKey | StorageKey[]>(key: K) {
    if (!key) {
      const err = new Error("missing key");
      this.logger.error({ err }, "missing key");
      throw err;
    }
    await backgroundClient.send(BgCommands.REMOVE_FROM_STORAGE, { key });
  }
  async clear(_options?: { excludeCollections?: StorageCollection[] }) {
    await backgroundClient.send(BgCommands.CLEAR_STORAGE);
  }
  async getIndexed<K extends StorageCollection>(collection: K, key: string) {
    if (!collection || !key) {
      const err = new Error("missing collection or key");
      this.logger.error({ err }, "missing collection or key");
      throw err;
    }
    return (await backgroundClient.send(BgCommands.GET_INDEXED_FROM_STORAGE, { collection, key })) as StorageCollectionValue<K> | undefined;
  }
  async setIndexed<K extends StorageCollection>(collection: K, key: string, data: StorageCollectionValue<K>) {
    if (!collection || !key) {
      const err = new Error("missing collection or key");
      this.logger.error({ err }, "missing collection or key");
      throw err;
    }
    await backgroundClient.send(BgCommands.SAVE_INDEXED_TO_STORAGE, { collection, key, data });
  }
}
